/**
 * The people in this workspace, and what can be done to each of them.
 *
 * The list is deliberately one table rather than two: an admin chasing a
 * stalled onboarding is looking for a person, not for a section. What differs
 * by state is the row's own detail line and its action menu, both of which live
 * in `members/MemberRow`.
 */

import { useState } from 'react';
import { Loader2, MailPlus, Search } from 'lucide-react';
import { toast } from 'sonner';
import {
  DESIGNATIONS,
  PERMISSIONS,
  SYSTEM_ROLES,
  type Designation,
  type SystemRole,
} from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { ApiError } from '@/lib/api';
import { useCan } from '@/features/auth';
import { MemberRow } from './members/MemberRow';
import { useInvitationSummaries, useInviteUser, useUsers, useWorkspace } from './api';

function humanise(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function InviteDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const invite = useInviteUser();
  const [email, setEmail] = useState('');
  const [systemRole, setSystemRole] = useState<SystemRole>('member');
  const [designation, setDesignation] = useState<Designation>('developer');
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    try {
      const result = await invite.mutateAsync({
        email: email.trim().toLowerCase(),
        systemRole,
        designation,
        teamIds: [],
        projectIds: [],
      });
      toast.success(result.resent ? 'Invitation resent' : `Invitation sent to ${email}`);
      setEmail('');
      onOpenChange(false);
    } catch (err) {
      setError(
        err instanceof ApiError ? (err.details?.[0]?.message ?? err.message) : 'Could not send the invite',
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle className="text-[16px] font-semibold">Invite someone</DialogTitle>
        <p className="mb-4 mt-0.5 text-[12px] vt-muted">
          They get an email with a single-use link that expires in 7 days.
        </p>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              autoFocus
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@lauruss.com"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="invite-role">System role</Label>
              <select
                id="invite-role"
                className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
                value={systemRole}
                onChange={(e) => setSystemRole(e.target.value as SystemRole)}
              >
                {SYSTEM_ROLES.filter((r) => r !== 'owner').map((r) => (
                  <option key={r} value={r}>
                    {humanise(r)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="invite-designation">Designation</Label>
              <select
                id="invite-designation"
                className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
                value={designation}
                onChange={(e) => setDesignation(e.target.value as Designation)}
              >
                {DESIGNATIONS.map((d) => (
                  <option key={d} value={d}>
                    {humanise(d)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <p className="text-[11px] vt-muted">
            Role controls what they may do. Designation is their craft — it sets default capacity and
            landing view, never permissions.
          </p>

          {error && (
            <p role="alert" className="rounded-[6px] bg-[var(--danger)]/10 px-2.5 py-2 text-[12px] text-[var(--danger)]">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submit} disabled={!email || invite.isPending}>
              {invite.isPending ? <Loader2 className="animate-spin" /> : <MailPlus />}
              Send invite
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function MembersPage() {
  const can = useCan();
  const [q, setQ] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const { data: people, isLoading } = useUsers(q ? { q } : {});
  const { data: invitations } = useInvitationSummaries();
  const { data: workspace } = useWorkspace();

  return (
    <>
      <PageHeader
        title="Members"
        subtitle="Roles control permissions. Designations describe the craft."
        actions={
          can(PERMISSIONS.USER_INVITE) ? (
            <Button variant="primary" onClick={() => setInviteOpen(true)}>
              <MailPlus />
              Invite
            </Button>
          ) : null
        }
        toolbar={
          <div className="relative max-w-[280px]">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or email"
              className="pl-7"
              aria-label="Search members"
            />
          </div>
        }
      />

      <div className="p-4">
        {isLoading ? (
          <div className="flex flex-col gap-2">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-11" />
            ))}
          </div>
        ) : !people || people.length === 0 ? (
          <EmptyState
            title={q ? 'Nobody matches that search' : 'No members yet'}
            description={q ? 'Try a different name or email.' : 'Invite your team to get started.'}
            action={
              can(PERMISSIONS.USER_INVITE) ? (
                <Button variant="primary" onClick={() => setInviteOpen(true)}>
                  <MailPlus />
                  Invite someone
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="vt-surface overflow-x-auto">
            <table className="w-full min-w-[680px] text-[13px]">
              <thead>
                <tr className="border-b border-[var(--border)] text-[11px] uppercase tracking-wide vt-muted">
                  <th className="py-2 pl-3 pr-2 text-left font-medium">Person</th>
                  <th className="px-2 py-2 text-left font-medium">Role</th>
                  <th className="px-2 py-2 text-left font-medium">Designation</th>
                  <th className="px-2 py-2 text-right font-medium">Capacity</th>
                  <th className="px-2 py-2 text-center font-medium">Push</th>
                  <th className="px-2 py-2 text-left font-medium">Status</th>
                  <th className="py-2 pl-2 pr-3" />
                </tr>
              </thead>
              <tbody>
                {people.map((person) => (
                  <MemberRow
                    key={person._id}
                    person={person}
                    invitation={invitations?.[person._id]}
                    ownerId={workspace?.ownerId}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </>
  );
}
